import { useEffect, useMemo, useState } from 'react';

import { clearCallDebugLog, subscribeCallDebugLog, type CallDebugLogEntry } from 'vcs-common';

import { Button, IconButton } from '../../../shared/ui';

import { isCallDebugEnabled } from './isCallDebugEnabled';

// A console palette on a black panel, not UI roles.
const LEVEL_COLOR: Record<CallDebugLogEntry['level'], string> = {
  info: '#9cdcfe',
  warn: '#ffd866',
  error: '#ff6b6b',
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${d.toLocaleTimeString('ru-RU', { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

/**
 * Диагностическая панель звонка: показывает поверх экрана события подписки на
 * треки (кто на кого подписался, когда создалась плитка участника, догнал ли
 * поздний трек уже смонтированный видеоэлемент). Нужна для тестирования на
 * iPad/iPhone/MacBook, где нет удобного доступа к devtools во время звонка.
 *
 * Открывается/скрывается плавающей кнопкой — сам по себе не занимает место,
 * пока не открыт.
 */
export function CallDebugPanel() {
  const [enabled] = useState(isCallDebugEnabled);
  if (!enabled) {
    return null;
  }

  return <CallDebugPanelContent />;
}

function CallDebugPanelContent() {
  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState<CallDebugLogEntry[]>([]);
  const [scopeFilter, setScopeFilter] = useState<string>('all');

  useEffect(() => {
    return subscribeCallDebugLog(setEntries);
  }, []);

  const scopes = useMemo(() => {
    const set = new Set<string>();
    entries.forEach((entry) => set.add(entry.scope));
    return Array.from(set);
  }, [entries]);

  const filteredEntries = useMemo(
    () => (scopeFilter === 'all' ? entries : entries.filter((entry) => entry.scope === scopeFilter)),
    [entries, scopeFilter],
  );

  return (
    <>
      <IconButton
        label={isOpen ? 'Скрыть диагностику звонка' : 'Показать диагностику звонка'}
        size="md"
        variant={isOpen ? 'primary' : 'overlay'}
        className="fixed z-toast bottom-[calc(132px+env(safe-area-inset-bottom,0px))] sm:bottom-[116px] lg:bottom-[144px] right-[10px] shadow-overlay"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        🐞
      </IconButton>

      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 z-toast h-[45vh] bg-black/90 text-white flex flex-col font-mono text-micro">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/20 shrink-0">
            <span className="font-medium">Диагностика звонка ({filteredEntries.length})</span>
            <select
              value={scopeFilter}
              onChange={(event) => setScopeFilter(event.target.value)}
              className="bg-white/10 rounded-chip px-2 py-1 text-white"
            >
              <option value="all">все ({entries.length})</option>
              {scopes.map((scope) => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
            <Button size="sm" variant="ghostInverse" className="ml-auto" onClick={() => clearCallDebugLog()}>
              Очистить
            </Button>
            <Button size="sm" variant="ghostInverse" onClick={() => setIsOpen(false)}>
              Закрыть
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 flex flex-col gap-1">
            {filteredEntries.length === 0 && <div className="text-white/50">Пока пусто</div>}
            {filteredEntries.map((entry) => (
              <div key={entry.id} className="leading-tight">
                <span className="text-white/40">{formatTime(entry.time)}</span>{' '}
                <span className="text-white/60">[{entry.scope}]</span>{' '}
                <span style={{ color: LEVEL_COLOR[entry.level] }}>{entry.message}</span>
                {entry.data && (
                  <div className="text-white/50 pl-4 whitespace-pre-wrap break-all">{JSON.stringify(entry.data)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
